import { RadioGroup } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/solid';
import { Link } from 'gatsby';
import * as React from 'react';
import { useState } from 'react';
import {
  Plan as PlanT,
  centsToUSD,
  frequencies,
  plans,
  savePercent,
} from '../../plans';
import { classNames } from '../../utils';

const LargePrice: React.FC<{ priceCents: number }> = ({ priceCents }) => (
  <>
    <span className="text-5xl">{centsToUSD(priceCents).split('.')[0]}</span>
    {centsToUSD(priceCents).includes('.') && (
      <span className="text-4xl">.{centsToUSD(priceCents).split('.')[1]}</span>
    )}
  </>
);

const Plan: React.FC<{ plan: PlanT; planIdx: number }> = ({
  plan,
  planIdx,
}) => {
  const [frequency, setFrequency] = useState(
    frequencies.find((f) => f.value === 'annually') || frequencies[0]
  );

  return (
    <div
      key={plan.id}
      className={classNames(
        plan.featured
          ? 'relative bg-white shadow-2xl'
          : 'bg-white sm:mx-8 lg:mx-0',
        plan.featured
          ? ''
          : planIdx === 0
            ? 'rounded-t-3xl sm:rounded-b-none lg:rounded-tr-none lg:rounded-bl-3xl'
            : 'sm:rounded-t-none lg:rounded-tr-3xl lg:rounded-bl-none',
        'rounded-3xl p-8 ring-1 ring-gray-900/10 sm:p-10'
      )}
    >
      <h3
        id={plan.id}
        className="text-base font-semibold leading-7 text-indigo-600"
      >
        {plan.name}
      </h3>
      <p className="mt-2 text-base leading-7 text-gray-600">
        {plan.description}
      </p>

      {plan.priceAnnuallyMonthlyCents && plan.priceMonthlyCents && (
        <div className="mt-4 flex justify-center">
          <RadioGroup
            value={frequency}
            onChange={setFrequency}
            className="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs font-semibold leading-5 ring-1 ring-inset ring-gray-300 bg-white"
          >
            <RadioGroup.Label className="sr-only">
              Payment frequency
            </RadioGroup.Label>
            {frequencies.map((option) => (
              <RadioGroup.Option
                key={option.value}
                value={option}
                className={({ checked }) =>
                  classNames(
                    checked ? 'bg-indigo-600 text-white' : 'text-gray-500',
                    'cursor-pointer rounded-full px-2 py-1'
                  )
                }
              >
                <span>{option.label}</span>
              </RadioGroup.Option>
            ))}
          </RadioGroup>
        </div>
      )}
      <p className="mt-4 flex items-baseline gap-x-2">
        <span className="text-5xl font-bold tracking-tight text-gray-900">
          {frequency.value === 'annually' ? (
            <LargePrice priceCents={plan.priceAnnuallyMonthlyCents} />
          ) : (
            <LargePrice priceCents={plan.priceMonthlyCents as number} />
          )}
        </span>
        <span className="text-base text-gray-500">/month</span>
        {frequency.value === 'annually' &&
          frequencies.length > 1 &&
          plan.priceAnnuallyMonthlyCents &&
          plan.priceMonthlyCents && (
            <span
              title="Compared to a monthly billed plan"
              className="rounded-full bg-indigo-600/10 px-2.5 py-1 self-start text-xs font-semibold leading-5 text-indigo-700"
            >
              Save{' '}
              {savePercent(
                plan.priceMonthlyCents,
                plan.priceAnnuallyMonthlyCents
              )}{' '}
              <span className="hidden sm:inline-block">with annual</span>
            </span>
          )}
      </p>
      <p className="mt-1 text-sm font-light leading-7 text-gray-600">
        {frequency.value === 'annually' && (
          <span>
            Billed annually {centsToUSD(plan.priceAnnuallyMonthlyCents * 12)}.
          </span>
        )}{' '}
        Plus tax.
      </p>
      <ul className="mt-6 space-y-3 text-sm leading-6 text-gray-600 sm:mt-8">
        {plan.features.map((feature) => (
          <li key={feature} className="flex gap-x-3">
            <CheckIcon
              className="h-6 w-5 flex-none text-indigo-600"
              aria-hidden="true"
            />
            {feature}
          </li>
        ))}
      </ul>
      <Link
        data-test={
          plan.id === 'tier-personal'
            ? 'pricing-personal-cta'
            : 'pricing-team-cta'
        }
        to={plan.href}
        aria-describedby={plan.id}
        className={classNames(
          plan.featured
            ? 'bg-indigo-600 text-white shadow hover:bg-indigo-500'
            : 'text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:ring-indigo-300',
          'mt-8 block rounded-md py-2.5 px-3.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:mt-10'
        )}
      >
        {plan.cta}
      </Link>
      {plan.belowCta && (
        <p className="mt-1 text-sm font-light leading-7 text-gray-600">
          {plan.belowCta}
        </p>
      )}
    </div>
  );
};

const Pricing = () => {
  return (
    <div className="isolate overflow-hidden bg-gray-200">
      <div
        className="mx-auto max-w-7xl px-6 pt-24 text-center sm:pt-32 lg:px-8"
        style={{ paddingBottom: '22rem' }}
      >
        <div className="mx-auto max-w-4xl">
          <h2
            id="pricing"
            className="font-extrabold text-gray-900 text-3xl sm:text-4xl"
          >
            Plans & pricing
          </h2>
        </div>
        <div className="relative mt-3">
          <p className="text-xl leading-9 text-gray-600">
            Simplify seating with our easy-to-use online application.
            <br />
            If you're not satisfied, contact us within the first{' '}
            <span className="text-2xl text-gray-700">14 days</span> and we'll
            send you a{' '}
            <span className="text-2xl text-gray-700">full refund</span>.
          </p>

          <svg
            viewBox="0 0 1208 800"
            className="absolute -top-10 left-1/2 -z-10 h-[50rem] -translate-x-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] sm:-top-12 md:-top-20 lg:-top-12 xl:top-0"
          >
            <ellipse
              cx={604}
              cy={512}
              fill="url(#6d1bd035-0dd1-437e-93fa-59d316231eb0)"
              rx={604}
              ry={512}
            />
            <defs>
              <radialGradient id="6d1bd035-0dd1-437e-93fa-59d316231eb0">
                <stop stopColor="#ffffff" />
                <stop offset={1} stopColor="#ffffff" />
              </radialGradient>
            </defs>
          </svg>
        </div>
      </div>
      <div className="flow-root bg-white pb-24 sm:pb-32">
        <div className="-mt-[25rem]">
          <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 items-center gap-y-6 sm:mt-20 sm:gap-y-0 lg:max-w-4xl lg:grid-cols-2">
            {plans.map((plan, planIdx) => (
              <Plan key={plan.id} plan={plan} planIdx={planIdx} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
